import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
];

const LanguageDropdown = ({ className = '', variant = 'default' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const getButtonStyles = () => {
    if (variant === 'light') {
      return {
        background: 'linear-gradient(to right, rgb(238 242 255), rgb(250 245 255))',
        border: '1px solid rgb(199 210 254)',
        color: 'rgb(109 40 217)',
      };
    }
    return {};
  };

  return (
    <div className={`lang-dropdown ${className}`} ref={dropdownRef}>
      <style>{`
        .lang-dropdown {
          position: relative;
          display: inline-block;
        }

        .lang-toggle-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: hsl(var(--muted));
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          color: hsl(var(--foreground) / 0.7);
          cursor: pointer;
          transition: all 0.2s;
        }

        .lang-toggle-btn:hover {
          background: hsl(var(--muted) / 0.8);
        }

        .lang-flag {
          font-size: 16px;
          line-height: 1;
        }

        .lang-chevron {
          transition: transform 0.2s ease;
        }

        .lang-chevron.open {
          transform: rotate(180deg);
        }

        .lang-dropdown-menu {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          min-width: 140px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          overflow: hidden;
        }

        .lang-option {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 14px;
          background: none;
          border: none;
          font-size: 14px;
          color: hsl(var(--foreground));
          cursor: pointer;
          transition: background-color 0.15s;
          text-align: left;
        }

        .lang-option:hover {
          background: hsl(var(--muted));
        }

        .lang-option.active {
          background: hsl(var(--primary) / 0.1);
          color: hsl(var(--primary));
          font-weight: 600;
        }
      `}</style>

      <button 
        className="lang-toggle-btn" 
        onClick={() => setIsOpen(!isOpen)} 
        data-testid="language-dropdown-btn"
        style={getButtonStyles()}
      >
        <span className="lang-flag">{currentLang.flag}</span>
        <span>{currentLang.code.toUpperCase()}</span>
        <ChevronDown size={14} className={`lang-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="lang-dropdown-menu">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              className={`lang-option ${lang.code === i18n.language ? 'active' : ''}`}
              onClick={() => changeLanguage(lang.code)}
            >
              <span className="lang-flag">{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;
